import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Button } from '@retainerclub/common';
import PropTypes from 'prop-types';
import SubscriptionActionAlert from '../ManageSubscriptionConfirmation/SubscriptionActionAlert';
import RetentionOffers from './RetentionOffers';

function ManageSubscriptionRetention({
  retentionOffers,
  handleRetentionOffer,
  setCurrentView,
  handleFinalCancel,
  isLoading,
  handlePauseAction,
  actionError,
}) {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-rccallouts-warning" />
        <h3 className="mt-4 font-semibold text-gray-900 text-lg">
          Don&apos;t lose access to fresh retainers!
        </h3>
      </div>

      <RetentionOffers
        retentionOffers={retentionOffers}
        handleRetentionOffer={handleRetentionOffer}
        isLoading={isLoading}
        display={false}
      />

      <div className="border-gray-200 border-t pt-4">
        <div>
          <p className="mb-4 text-gray-600 text-md">
            Without regular replacement, your retainers lose effectiveness and
            your treatment results may vary
          </p>

          <div className="mb-6 text-left">
            <h4 className="mb-3 font-semibold text-gray-900">
              Why doctors recommend regular replacement
            </h4>
            <ul className="space-y-2 text-gray-600 text-sm">
              <li className="flex items-start">
                <span className="mr-2 text-rccallouts-primary">•</span>
                <span>
                  <strong>Better Fit:</strong> New retainers are made from your
                  most recent scan
                </span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 text-rccallouts-primary">•</span>
                <span>
                  <strong>Cleaner, Fresher:</strong> For optimal oral health,
                  replace your retainers at least once every year
                </span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 text-rccallouts-primary">•</span>
                <span>
                  <strong>Consistent Results:</strong> Regular replacement is
                  the best way to maintain results from the best performing
                  retainers
                </span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 text-rccallouts-primary">•</span>
                <span>
                  <strong>Best Pricing:</strong> Like most things these days,
                  prices increase over time. By purchasing now, you are getting
                  the best pricing
                </span>
              </li>
            </ul>
            <h4 className="mb-3 font-semibold text-gray-900">Did you know?</h4>
            <p className="text-gray-600 text-sm">
              By purchasing from our Gold or Platinum plans, you are paying less
              per set!
            </p>
          </div>

          <div className="mb-6 border-rcgray-400 border-t pt-4 pb-8">
            <h4 className="mb-3 font-semibold text-gray-900">
              Need a break? Pause your renewal
            </h4>
            <p className="mb-4 text-gray-600 text-sm">
              Pausing will delay your renewal for either 3 or 6 months (90 or
              180 days). Once the pause ends, regular shipments and billing will
              resume.
            </p>
            <div className="flex space-x-4 pt-4">
              <button
                type="button"
                onClick={() => handlePauseAction(90)}
                disabled={isLoading}
                className="flex-1 cursor-pointer rounded-xl border border-green-600 bg-transparent px-3 py-2 text-center font-medium text-green-600 shadow-xs hover:text-green-500 focus-visible:outline-2 focus-visible:outline-green-600 focus-visible:outline-solid focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Pause for 3 months
                <span className="block font-normal text-gray-400 text-xs italic">
                  *90 days
                </span>
              </button>
              <button
                type="button"
                onClick={() => handlePauseAction(180)}
                disabled={isLoading}
                className="flex-1 cursor-pointer rounded-xl border border-green-600 bg-transparent px-3 py-2 text-center font-medium text-green-600 shadow-xs hover:text-green-500 focus-visible:outline-2 focus-visible:outline-green-600 focus-visible:outline-solid focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Pause for 6 months
                <span className="block font-normal text-gray-400 text-xs italic">
                  *180 days
                </span>
              </button>
            </div>
          </div>

          <SubscriptionActionAlert actionError={actionError} />

          <div className="flex space-x-4 border-rcgray-400 border-t pt-8">
            <Button
              name="Cancel Subscription"
              size="md"
              color="redOutline"
              onClick={handleFinalCancel}
              disabled={isLoading}
              rounded
              className="flex-1 justify-center"
            />
            <Button
              name="Go Back"
              size="md"
              color="rcprimary"
              rounded
              onClick={() => setCurrentView('main')}
              className="flex-1 justify-center"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

ManageSubscriptionRetention.propTypes = {
  retentionOffers: PropTypes.array.isRequired,
  handleRetentionOffer: PropTypes.func.isRequired,
  setCurrentView: PropTypes.func.isRequired,
  handleFinalCancel: PropTypes.func.isRequired,
  handlePauseSubscription: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  handlePauseAction: PropTypes.func.isRequired,
  handleUnpauseAction: PropTypes.func.isRequired,
  actionError: PropTypes.object,
};

export default ManageSubscriptionRetention;
